import React, { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { ChevronDown, ChevronUp, Search, MessageSquare, Star, Zap, Building2, Users, Shield, Phone, ArrowRight, HelpCircle, CheckCircle, Clock, Globe, Settings, HeartHandshake } from 'lucide-react';

const FAQ = () => {
  const [expandedCategory, setExpandedCategory] = useState('getting-started');
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');

  const handleBookDemo = () => {
    const element = document.getElementById('contact');
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const faqCategories = [
    {
      id: 'getting-started',
      title: 'Getting Started',
      icon: Zap,
      color: 'from-[#2D6A4F] to-[#1F4A36]',
      description: 'Setup, requirements, and first steps',
      faqs: [
        {
          question: "What's the difference between 30-day trial and money-back guarantee?",
          answer: "They're the same thing. Try PrismoSpark for 30 days. If you don't see results, we refund 100%. No fine print."
        },
        {
          question: "How fast is setup?",
          answer: "Growth Accelerator: 2-3 weeks (we build everything custom for your restaurant). Complete Restaurant System: 2-3 weeks (complete rebuild takes time). Multi-Location: 3-4 weeks."
        },
        {
          question: "What do you need from me?",
          answer: "Restaurant info (name, address, phone, hours), menu (PDF or doc), 10-15 photos of food and space, logo, Google Business Profile access. We guide you step-by-step."
        },
        {
          question: "Do I need technical knowledge?",
          answer: "No. We handle 100% of setup. You get a simple dashboard to track results."
        },
        {
          question: "Can I try Isabella before committing?",
          answer: "Yes. [Chat with Isabella demo] on our website. Watch her handle customer questions in real-time."
        }
      ]
    },
    {
      id: 'isabella-ai',
      title: 'Isabella AI Assistant',
      icon: MessageSquare,
      color: 'from-[#D4623C] to-[#B8532F]',
      description: 'AI assistant capabilities and features',
      faqs: [
        {
          question: "How does Isabella work?",
          answer: "Isabella is trained on restaurant conversations and your specific brand. She handles bookings 24/7, answers FAQs, supports multiple languages, and escalates complex requests to your team."
        },
        {
          question: "Will Isabella sound robotic?",
          answer: "No. We train her on your restaurant's personality. Most customers don't realize they're talking to AI. Average satisfaction rating: 4.7/5."
        },
        {
          question: "What if Isabella doesn't know the answer?",
          answer: "She says \"Let me connect you with the team\" and routes to your staff with full conversation context. No frustrated customers."
        },
        {
          question: "Can Isabella handle phone calls?",
          answer: "Currently text/chat only (website widget, SMS, WhatsApp, Facebook Messenger). Voice calling coming Q2 2026."
        },
        {
          question: "What languages does Isabella speak?",
          answer: "English, Spanish, French, Hindi, Punjabi, Mandarin. More languages will be added soon."
        },
        {
          question: "Can I customize Isabella's responses?",
          answer: "Yes. You review and approve her knowledge base during setup. Update anytime through dashboard."
        },
        {
          question: "What booking systems does Isabella integrate with?",
          answer: "OpenTable, Resy, Yelp Reservations, SevenRooms, Toast Tables, Eat App, TableAgent, Wisely, Tock, and custom systems."
        }
      ]
    },
    {
      id: 'review-accelerator',
      title: 'Review Accelerator',
      icon: Star,
      color: 'from-[#2D6A4F] to-[#1F4A36]',
      description: 'Review management and automation',
      faqs: [
        {
          question: "How do you get customers to leave reviews?",
          answer: "Automated SMS sent 2 hours after dining. One-tap link makes it effortless. Response rate: 12-18% (vs. 1-2% for manual requests)."
        },
        {
          question: "What about negative reviews?",
          answer: "Automated AI-drafted professional response posted immediately. For serious issues, you can override and customize the response."
        },
        {
          question: "Will this look spammy?",
          answer: "No. Single polite message. If no response, no follow-up. Unsubscribe option included. Compliant with TCPA, CAN-SPAM, CASL."
        },
        {
          question: "What if someone leaves a fake review?",
          answer: "We help you report to Google with evidence. Include templates and step-by-step flagging process."
        }
      ]
    },
    {
      id: 'local-seo',
      title: 'Local SEO',
      icon: Globe,
      color: 'from-[#E6B84D] to-[#D4A935]',
      description: 'Search engine optimization',
      faqs: [
        {
          question: "How fast will SEO work?",
          answer: "Week 1-2: Google Business optimization and quick wins. Month 1-3: Steady ranking improvements. Month 3-6: Significant visibility gains for target keywords. SEO is cumulative—results compound over time."
        },
        {
          question: "What if I'm already ranking well?",
          answer: "We identify untapped keyword opportunities, expand to nearby neighborhoods, protect rankings from competitors."
        },
        {
          question: "Do you guarantee #1 rankings?",
          answer: "No one can guarantee specific rankings (Google changes constantly). We guarantee measurable improvement in visibility and \"near me\" traffic."
        },
        {
          question: "What happens if I cancel?",
          answer: "Your website stays live (if Complete Restaurant System). Reviews stay on Google forever. SEO rankings gradually decline without ongoing optimization."
        }
      ]
    },
    {
      id: 'pricing-plans',
      title: 'Pricing & Plans',
      icon: Building2,
      color: 'from-[#2D6A4F] to-[#1F4A36]',
      description: 'Billing, payments, and plan details',
      faqs: [
        {
          question: "Why is there a setup fee for Complete Restaurant System?",
          answer: "Covers custom website design, menu build, photography optimization, booking integration, SEO foundation. 15-20 hours of work. Waived if you pay annually."
        },
        {
          question: "Can I pause my subscription?",
          answer: "No pause option, but you can cancel with 30 days' notice and restart later. Setup fees apply again on restart."
        },
        {
          question: "What payment methods do you accept?",
          answer: "Credit card (Visa, Mastercard, Amex), ACH bank transfer (US only), wire transfer (international)."
        },
        {
          question: "Do you offer discounts?",
          answer: "Annual plans save 2 months + waived setup fee ($2,497 value). Multi-location discounts available. No other discounts."
        },
        {
          question: "What's included in Multi-Location Package?",
          answer: "Everything in Complete Restaurant System for each location + centralized dashboard + dedicated account manager + quarterly strategy calls + phone support."
        }
      ]
    },
    {
      id: 'technical-security',
      title: 'Technical & Security',
      icon: Shield,
      color: 'from-[#2D6A4F] to-[#1F4A36]',
      description: 'Integrations, data, and security',
      faqs: [
        {
          question: "What platforms do you integrate with?",
          answer: "Booking: OpenTable, Resy, Toast, SevenRooms, Eat App. POS: Square, Toast, Clover, Lightspeed. Reviews: Google. Most major restaurant systems."
        },
        {
          question: "Is my data secure?",
          answer: "Yes. Bank-level 256-bit SSL encryption, daily automated backups, GDPR and CCPA compliant, SOC 2 Type II certified, 99.9% uptime SLA."
        },
        {
          question: "Can I export my data?",
          answer: "Yes. Export all analytics, reviews, Isabella conversations, customer data anytime as CSV or JSON."
        },
        {
          question: "Do you work with my existing website?",
          answer: "Yes. Growth Accelerator integrates with WordPress, Wix, Squarespace, Shopify, Webflow, and custom sites. Simple embed code—no rebuild needed."
        },
        {
          question: "What about website hosting?",
          answer: "Included in Complete Restaurant System. Cloud hosting with global CDN, automatic SSL, daily backups, DDoS protection."
        }
      ]
    },
    {
      id: 'support-training',
      title: 'Support & Training',
      icon: HeartHandshake,
      color: 'from-[#D4623C] to-[#E6B84D]',
      description: 'Help, training, and consulting',
      faqs: [
        {
          question: "What support do I get?",
          answer: "Growth Accelerator: Email support (24-hour response). Complete Restaurant System: Priority email + live chat (4-hour response). Multi-Location: Dedicated manager + phone support."
        },
        {
          question: "Do you provide training?",
          answer: "Yes. We handle everything for you—from setup to ongoing management. No technical knowledge needed."
        },
        {
          question: "Can you update my menu?",
          answer: "Complete Restaurant System: 2 menu updates/month included. Additional changes: $47/hour."
        },
        {
          question: "What if I need help outside business hours?",
          answer: "Email/chat available 24/7. Urgent issues (site down, Isabella malfunction) get priority response within 2 hours."
        },
        {
          question: "Do you offer strategy consulting?",
          answer: "Multi-Location packages include quarterly strategy calls. Other plans: $200/hour for consulting (reservation strategy, pricing optimization, competitive analysis)."
        }
      ]
    }
  ];

  const toggleCategory = (categoryId) => {
    setExpandedCategory(expandedCategory === categoryId ? null : categoryId);
  };

  const toggleFaq = (categoryId, faqIndex) => {
    const faqId = `${categoryId}-${faqIndex}`;
    setExpandedFaq(expandedFaq === faqId ? null : faqId);
  };

  // Filter FAQs based on search query
  const filteredCategories = faqCategories.map(category => ({
    ...category,
    faqs: category.faqs.filter(faq => 
      searchQuery === '' || 
      faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
    )
  })).filter(category => category.faqs.length > 0);

  return (
    <div className="pt-16 overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-screen bg-gradient-to-br from-restaurant-dark-900 via-restaurant-dark-800 to-restaurant-dark-700">
        {/* Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-20 right-20 w-64 h-64 bg-gradient-to-br from-forest-primary/20 to-forest-primary-600/20 rounded-full blur-3xl animate-pulse"></div>
          <div className="absolute bottom-20 left-20 w-64 h-64 bg-gradient-to-br from-sienna-accent/20 to-brand-gold/20 rounded-full blur-3xl animate-pulse delay-1000"></div>
          <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-48 h-48 bg-gradient-to-br from-forest-primary/20 to-forest-primary/20 rounded-full blur-3xl animate-pulse delay-500"></div>
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32 text-center">
          <Badge className="inline-block mb-8 bg-white/10 backdrop-blur-md border border-white/20 text-white px-8 py-4 text-lg font-medium">
            💡 Frequently Asked Questions
          </Badge>
          <h1 className="text-5xl lg:text-7xl font-bold text-white mb-8 leading-tight">
            Everything You Need
            <span className="block bg-gradient-to-r from-forest-primary to-forest-primary-600 bg-clip-text text-transparent animate-gradient bg-300%">
              to Know
            </span>
          </h1>
          <p className="text-xl text-brand-ivory/90 max-w-4xl mx-auto mb-12 leading-relaxed">
            Get instant answers to the most common questions about PrismoSpark. Can't find what you're looking for? We're here to help.
          </p>
          
          {/* Search Bar */}
          <div className="max-w-2xl mx-auto mb-16">
            <div className="relative">
              <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-brand-ivory/60" size={20} />
              <input
                type="text"
                placeholder="Search FAQs..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full bg-white/10 backdrop-blur-md border border-white/20 rounded-2xl pl-12 pr-4 py-4 text-white placeholder-brand-ivory/60 focus:outline-none focus:border-forest-primary focus:bg-white/20 transition-all duration-300"
              />
            </div>
          </div>

          {/* Category Overview */}
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 max-w-6xl mx-auto">
            {faqCategories.map((category, index) => (
              <div key={category.id} className="group cursor-pointer" onClick={() => {
                document.getElementById('faq-content').scrollIntoView({ behavior: 'smooth' });
                setExpandedCategory(category.id);
              }}>
                <div className={`w-full h-20 bg-gradient-to-br ${category.color} rounded-2xl flex items-center justify-center transform group-hover:scale-110 group-hover:-translate-y-2 transition-all duration-500 shadow-lg backdrop-blur-sm border border-white/20`}>
                  <category.icon className="text-white" size={24} />
                </div>
                <div className="text-white/80 text-sm mt-3 group-hover:text-white transition-colors duration-300 text-center font-medium">
                  {category.title.split(' ')[0]}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section id="faq-content" className="py-32 bg-gradient-to-br from-brand-ivory via-brand-ivory-50 to-brand-ivory">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-4 gap-8">
            {/* Category Sidebar */}
            <div className="lg:col-span-1">
              <div className="sticky top-24 space-y-4">
                <h3 className="text-xl font-bold text-brand-charcoal mb-6">Categories</h3>
                {faqCategories.map((category) => (
                  <button
                    key={category.id}
                    onClick={() => setExpandedCategory(category.id)}
                    className={`w-full text-left p-4 rounded-xl border-2 transition-all duration-300 ${
                      expandedCategory === category.id 
                        ? 'border-brand-charcoal bg-brand-ivory-50 shadow-lg' 
                        : 'border-brand-charcoal/20 hover:border-brand-charcoal/30 hover:bg-brand-ivory-50'
                    }`}
                  >
                    <div className="flex items-center space-x-3">
                      <div className={`w-10 h-10 bg-gradient-to-br ${category.color} rounded-lg flex items-center justify-center`}>
                        <category.icon className="text-white" size={18} />
                      </div>
                      <div>
                        <div className="font-semibold text-brand-charcoal text-sm">{category.title}</div>
                        <div className="text-xs text-brand-charcoal-600">{category.description}</div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {/* FAQ Content */}
            <div className="lg:col-span-3">
              {filteredCategories.map((category) => (
                <div key={category.id} className={`mb-16 ${expandedCategory !== category.id && searchQuery === '' ? 'hidden' : ''}`}>
                  <div className="flex items-center space-x-4 mb-8">
                    <div className={`w-16 h-16 bg-gradient-to-br ${category.color} rounded-2xl flex items-center justify-center shadow-lg`}>
                      <category.icon className="text-white" size={28} />
                    </div>
                    <div>
                      <h2 className="text-3xl font-bold text-brand-charcoal">{category.title}</h2>
                      <p className="text-brand-charcoal-600">{category.description}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    {category.faqs.map((faq, index) => {
                      const faqId = `${category.id}-${index}`;
                      const isExpanded = expandedFaq === faqId;
                      
                      return (
                        <Card key={index} className="border border-brand-charcoal/20 hover:shadow-lg transition-all duration-300">
                          <CardHeader
                            className="cursor-pointer hover:bg-brand-ivory-50 transition-colors duration-200"
                            onClick={() => toggleFaq(category.id, index)}
                          >
                            <div className="flex items-center justify-between">
                              <CardTitle className="text-lg text-brand-charcoal pr-4">{faq.question}</CardTitle>
                              {isExpanded ? (
                                <ChevronUp className="text-brand-charcoal-600 flex-shrink-0" size={24} />
                              ) : (
                                <ChevronDown className="text-brand-charcoal-600 flex-shrink-0" size={24} />
                              )}
                            </div>
                          </CardHeader>
                          {isExpanded && (
                            <CardContent className="pt-0">
                              <p className="text-brand-charcoal-600 leading-relaxed">{faq.answer}</p>
                            </CardContent>
                          )}
                        </Card>
                      );
                    })}
                  </div>
                </div>
              ))}

              {searchQuery !== '' && filteredCategories.length === 0 && (
                <div className="text-center py-20">
                  <HelpCircle className="mx-auto text-brand-charcoal/40 mb-4" size={64} />
                  <h3 className="text-xl font-semibold text-brand-charcoal mb-2">No results found</h3>
                  <p className="text-brand-charcoal-600 mb-6">Try searching with different keywords or browse our categories above.</p>
                  <Button onClick={() => setSearchQuery('')} variant="outline" className="border-brand-charcoal/30 text-brand-charcoal hover:bg-brand-charcoal/5">
                    Clear Search
                  </Button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Still Have Questions CTA */}
      <section className="py-32 bg-gradient-to-r from-restaurant-dark-900 to-restaurant-dark-800">
        <div className="max-w-4xl mx-auto text-center px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl lg:text-6xl font-bold text-white mb-8">
            Still Have
            <span className="block bg-gradient-to-r from-forest-primary to-forest-primary-600 bg-clip-text text-transparent">
              Questions?
            </span>
          </h2>
          <p className="text-xl text-brand-ivory/90 mb-12 leading-relaxed">
            Can't find the answer you're looking for? Our team is here to help you every step of the way.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-6 justify-center items-center">
            <Button 
              onClick={handleBookDemo}
              className="group relative font-bold text-xl px-12 py-6 rounded-2xl shadow-2xl transition-all duration-300 transform hover:-translate-y-1 hover:scale-[1.02] border-0 overflow-hidden"
              style={{
                backgroundColor: '#D4623C',
                color: '#FFFFFF',
                boxShadow: '0 25px 50px -12px rgba(212, 98, 60, 0.25)'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#B8532F';
                e.target.style.boxShadow = '0 25px 50px -12px rgba(212, 98, 60, 0.4)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#D4623C';
                e.target.style.boxShadow = '0 25px 50px -12px rgba(212, 98, 60, 0.25)';
              }}
            >
              {/* Animated background layer */}
              <div className="absolute inset-0 bg-gradient-to-br from-white/20 via-transparent to-white/20 opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
              
              {/* Shimmer effect */}
              <div className="absolute inset-0 -translate-x-full group-hover:translate-x-full bg-gradient-to-r from-transparent via-white/30 to-transparent transition-transform duration-700 ease-in-out"></div>
              
              {/* Content */}
              <div className="relative z-10 flex items-center justify-center space-x-3">
                <MessageSquare className="w-6 h-6 group-hover:animate-pulse" />
                <span>Talk to Our Team</span>
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
              </div>
              
              {/* Glow effect */}
              <div className="absolute inset-0 rounded-2xl bg-[#2D6A4F]/50 blur-xl opacity-0 group-hover:opacity-70 transition-opacity duration-500 -z-10 scale-110"></div>
            </Button>
            
            <div className="text-brand-ivory/80 text-center">
              <div className="text-sm mb-2">💬 Live chat available</div>
              <div className="text-sm">📞 (555) 123-GROW</div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section Placeholder for Scroll Target */}
      <section id="contact" className="py-20 bg-gradient-to-br from-brand-ivory-50 to-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-5xl font-bold text-brand-charcoal mb-8">
            Get in Touch
          </h2>
          <p className="text-xl text-brand-charcoal-600 mb-8 leading-relaxed">
            Ready to get started or need personalized help? Contact us today.
          </p>
          <div className="bg-white p-8 rounded-2xl shadow-2xl border border-brand-charcoal/20">
            <p className="text-lg text-brand-charcoal-600 mb-4">
              📞 Phone: <span className="font-semibold">(555) 123-GROW</span>
            </p>
            <p className="text-lg text-brand-charcoal-600 mb-4">
              ✉️ Email: <span className="font-semibold">hello@prismospark.com</span>
            </p>
            <p className="text-base text-brand-charcoal-500">
              Our team is available Monday-Friday, 9 AM - 6 PM EST
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;